import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFieldArray, useForm } from 'react-hook-form';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { actualValueForDropdown, debounce, getAssetColor } from '../../../utils/common';
import scogo_apis from '../../../scogo_apis';
import AssetType from '../../../components/Inputs/AssetType';
import { useDispatch } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import { useSelector } from 'react-redux';
import { addBoqInServiceRequest, removeAssetFromTicket } from '../../../actions/service_request';
import { sendToastMessage } from '../../../actions/toast';
import { SEND_TOAST } from '../../../types/toast';
import RenderMilestones from '../components/RenderMileStones';
import IconToolTip from '../../../common/IconToolTip';
import { copyToClipboard } from '../../../actions/utils';
import PavDynamicAssetField from '../../../features/pav/PavDynamicAssetField';
import { serializePavAssetFieldValue } from '../../../features/pav/pavAssetUtils';


const assetsArrayName = 'assetsArrayName';
const formName = 'assetsArrayNameFormName';

export const AddTicketAsset = ({ ticket, isRma = false }) => {
    const [selectedMilestones, setSelectedMilestones] = useState();
    const [ticketAssets, setTicketAssets] = useState([]);
    const methods = useForm({
        defaultValues: {
            [assetsArrayName]: [{ asset_type_id: null }]
        }, mode: 'onChange'
    });
    const { control, setValue, watch, formState, append } = methods;

    const { fields, remove } = useFieldArray({
        control,
        name: assetsArrayName,
    });
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);

    useEffect(() => {
        if (Array.isArray(ticket.assets)) {
            setTicketAssets([...ticket.assets]);
        }
    }, [ticket.assets]);

    const availableAsset = useMemo(() => {
        return ticketAssets;
    }, [ticketAssets]);

    const submit = (formValues) => {
        const list = formValues[assetsArrayName];
        const data = list.length > 0 ? list[0] : undefined;
        if (!data) {
            dispatch(sendToastMessage({ type: SEND_TOAST, payload: { message: 'Please fill input', status: 'warning' } }))
        }
        const payload = {
            asset_type_id: actualValueForDropdown(data.asset_type_id),
            milestone_id: selectedMilestones,
            ticket_id: ticket.id
        };

        if (data.asset_id) payload.asset_id = data.asset_id;

        let additional_fields = [];
        data.asset_type_id.fields.forEach(field => {
            additional_fields.push({
                key: field.key,
                value: serializePavAssetFieldValue(field, data[field.key])
            });
        });
        payload.additional_fields = additional_fields;

        dispatch(addBoqInServiceRequest({
            formName, payload, onSuccess: () => {
                methods.reset();
            },
            closeModal: true
        }));
    };

    const handleRemoveAsset = (assetId) => {
        if (assetId && ticket.id) {
            dispatch(removeAssetFromTicket({
                id: ticket.id,
                asset_id: assetId,
                formName: `removeAssetFromTicket${assetId}`,
                onSuccess: () => {
                    setTicketAssets(prevAssets => prevAssets.filter(asset => asset.asset_id !== assetId));
                }
            }));
        }
    };

    return <Form
        methods={methods}
        onSubmit={submit}
        submitButtonText={'Create'}
        onCancel={() => dispatch(closeModalAction())}
        showSubmit
        submitContainerClassName='flex gap-2 justify-end py-8 px-6'
        isLoading={formButtonLoading[formName]}
    >
        <div className='px-4 py-2'>
            <RenderMilestones milestones={ticket.milestones} selectedMilestones={selectedMilestones} setSelectedMilestones={setSelectedMilestones} agents={ticket.agents} />
            {(isRma && availableAsset.length > 0) ? (
                <div className="flex flex-wrap gap-2 px-1 py-2 max-h-[10rem] overflow-y-auto">
                    <span className='text-scogoprimary text-font14'>Available Assets </span>
                    {availableAsset.map((asset, index) => {
                        const status = asset?.status?.length > 0 ? ` ${asset.status}` : "";
                        const workingStatus = asset?.working_condition?.length > 0 ? `, ${asset.working_condition}` : "";
                        const tooltipText = `${status}${workingStatus}`;
                        const serialOrTitle = asset?.asset?.serial_number?.length > 0
                            ? asset.asset.serial_number
                            : asset?.asset?.asset_type?.title;

                        return (
                            <IconToolTip key={index} title={tooltipText}>
                                <div className={`flex items-center bg-${getAssetColor(asset)} text-white text-font10 px-2 py-1 rounded-3px cursor-pointer max-w-[160px] hover:shadow-md transition-all duration-200`}>
                                    <span className="truncate flex-1 font-medium">
                                        {serialOrTitle}
                                        {asset?.count > 1 && <span className="font-bold">{`: ${asset.count}`}</span>}
                                    </span>
                                    <div className="flex items-center pt-1">
                                        <span
                                            className="material-icons text-font12 ml-1 hover:text-scogobgsky transition-colors duration-200 p-0.5 hover:bg-white hover:bg-opacity-20 rounded-full"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                dispatch(copyToClipboard({ data: serialOrTitle }));
                                            }}
                                        >
                                            content_copy
                                        </span>
                                        <span
                                            className={`material-icons text-font12 ml-1 text-red-500 hover:text-red-700 cursor-pointer transition-colors duration-200 p-0.5 hover:bg-white hover:bg-opacity-20 rounded-full ${formButtonLoading[`removeAssetFromTicket${asset?.asset_id}`] ? 'animate-spin' : ''}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleRemoveAsset(asset?.asset_id);
                                            }}
                                        >
                                            {formButtonLoading[`removeAssetFromTicket${asset?.asset_id}`] ? 'refresh' : 'delete'}
                                        </span>
                                    </div>
                                </div>
                            </IconToolTip>
                        );
                    })}
                </div>
            ) : null}
        </div>
        <div className='flex flex-col gap-2 flex-1 h-full px-2 py-2'>
            {
                fields.map((field, index) => {
                    const prefix = `${assetsArrayName}[${index}]`;
                    const assetType = watch(`${prefix}.asset_type_id`);

                    return (
                        <motion.div
                            key={field.id}
                            className="border-b border-gray-200 rounded-lg bg-gray-50 transition-colors duration-200 px-3"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="flex justify-between items-center mb-1">

                                <h3 className="text-md font-medium text-gray-700 flex items-center">
                                    <span className="material-icons-outlined mr-2 text-scogoprimary" style={{ fontSize: '18px' }}>
                                        devices
                                    </span>
                                    Asset {index + 1}
                                </h3>
                                {fields.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                                    >
                                        <span className="material-icons-outlined">delete</span>
                                    </button>
                                )}
                            </div>

                            <motion.div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2'>
                                <motion.div
                                    className="md:col-span-2 lg:col-span-1"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.1 }}
                                >

                                    <AssetType
                                        label='Asset Type'
                                        name={`${prefix}.asset_type_id`}
                                        org_id={ticket.org_id}
                                        className='w-full'
                                        prefill={[]}
                                    />
                                </motion.div>
                                {assetType?.fields?.map((fieldItem, fieldIndex) => {
                                    if (fieldItem.key === 'serial_number') {
                                        return (
                                            <motion.div
                                                key={fieldItem.key}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.1 + (fieldIndex * 0.05) }}
                                            >
                                                <Input
                                                    required={fieldItem.is_required}
                                                    name={`${prefix}.${fieldItem.key}`}
                                                    label={fieldItem.label}
                                                    className='w-full'
                                                    validate={{
                                                        serialNumberNotInSystem: (value) => {
                                                            if (value === '' || formState?.isSubmitting) return true;

                                                            return new Promise(resolve => {
                                                                const validateSerialNumber = async () => {
                                                                    try {
                                                                        const response = await scogo_apis.inventory.checkAsset({}, {}, {}, { serial_number: value, org_id: ticket.org_id });
                                                                        if (response.status === 201 && response?.data?.data?.asset_type?.title) {
                                                                            if (response?.data?.data?.org_id === ticket.org_id) {
                                                                                setValue(`${prefix}.asset_id`, response.data.data.id);
                                                                                setValue(`${prefix}.make`, response.data.data.make);
                                                                                setValue(`${prefix}.model`, response.data.data.model);
                                                                                setValue(`${prefix}.partcode`, response.data.data.partcode);
                                                                                setValue(`${prefix}.validating`, false);
                                                                                setValue(`${prefix}.asset_not_found`, false);

                                                                                if (response.data.data.asset_type.fields && response.data.data.asset_type.fields.length > 0) {
                                                                                    setValue(`${prefix}.asset_type_id`, {
                                                                                        key: response.data.data.asset_type.id,
                                                                                        label: response.data.data.asset_type.title,
                                                                                        fields: response.data.data.asset_type.fields
                                                                                    });
                                                                                } else {
                                                                                    try {
                                                                                        const assetTypeResponse = await scogo_apis.inventory.getAssetTypeList({}, {}, { org_id: ticket.org_id, q: response.data.data.asset_type.title });
                                                                                        if (assetTypeResponse.status === 200) {
                                                                                            const assetTypeWithFields = assetTypeResponse.data.find(type => type.id === response.data.data.asset_type.id);
                                                                                            if (assetTypeWithFields) {
                                                                                                setValue(`${prefix}.asset_type_id`, {
                                                                                                    key: assetTypeWithFields.id,
                                                                                                    label: assetTypeWithFields.title,
                                                                                                    fields: assetTypeWithFields.additional_fields || []
                                                                                                });
                                                                                            } else {
                                                                                                setValue(`${prefix}.asset_type_id`, {
                                                                                                    key: response.data.data.asset_type.id,
                                                                                                    label: response.data.data.asset_type.title,
                                                                                                    fields: []
                                                                                                });
                                                                                            }
                                                                                        } else {
                                                                                            setValue(`${prefix}.asset_type_id`, {
                                                                                                key: response.data.data.asset_type.id,
                                                                                                label: response.data.data.asset_type.title,
                                                                                                fields: []
                                                                                            });
                                                                                        }
                                                                                    } catch (assetTypeError) {
                                                                                        console.error("Error fetching asset type details:", assetTypeError);
                                                                                        setValue(`${prefix}.asset_type_id`, {
                                                                                            key: response.data.data.asset_type.id,
                                                                                            label: response.data.data.asset_type.title,
                                                                                            fields: []
                                                                                        });
                                                                                    }
                                                                                }
                                                                                resolve(true);
                                                                                return;
                                                                            }
                                                                            setValue(`${prefix}.asset_id`, null);
                                                                            setValue(`${prefix}.make`, null);
                                                                            setValue(`${prefix}.model`, null);
                                                                            setValue(`${prefix}.partcode`, null);
                                                                            setValue(`${prefix}.validating`, false);
                                                                            setValue(`${prefix}.asset_not_found`, true);
                                                                            resolve("Asset doesn't belong to your organization.");
                                                                            return;
                                                                        }
                                                                        if (response.status === 201 && response.data.data.id) {
                                                                            setValue(`${prefix}.asset_id`, response.data.data.id);
                                                                            setValue(`${prefix}.make`, response.data.data.make);
                                                                            setValue(`${prefix}.model`, response.data.data.model);
                                                                            setValue(`${prefix}.partcode`, response.data.data.partcode);
                                                                            setValue(`${prefix}.validating`, false);
                                                                            setValue(`${prefix}.asset_not_found`, false);

                                                                            // If asset type exists, try to fetch its fields
                                                                            if (response.data.data.asset_type) {
                                                                                try {
                                                                                    const assetTypeResponse = await scogo_apis.inventory.getAssetTypeList({}, {}, { org_id: ticket.org_id });
                                                                                    if (assetTypeResponse.status === 200) {
                                                                                        const assetTypeWithFields = assetTypeResponse.data.find(type => type.id === response.data.data.asset_type.id);
                                                                                        if (assetTypeWithFields) {
                                                                                            setValue(`${prefix}.asset_type_id`, {
                                                                                                key: assetTypeWithFields.id,
                                                                                                label: assetTypeWithFields.title,
                                                                                                fields: assetTypeWithFields.additional_fields || []
                                                                                            });
                                                                                        }
                                                                                    }
                                                                                } catch (assetTypeError) {
                                                                                    console.error("Error fetching asset type details:", assetTypeError);
                                                                                }
                                                                            }
                                                                            resolve(true);
                                                                        } else {
                                                                            setValue(`${prefix}.validating`, false);
                                                                            setValue(`${prefix}.asset_not_found`, true);
                                                                            resolve("Serial Number doesn't exist in the System.");
                                                                        }
                                                                    } catch (error) {
                                                                        if (!ticket.organization.is_inventory_applicable) {
                                                                            setValue(`${prefix}.validating`, false);
                                                                            resolve(true);
                                                                            return;
                                                                        }
                                                                        console.error("Error checking serial number:", error);
                                                                        setValue(`${prefix}.asset_not_found`, true);
                                                                        resolve(error?.response?.data?.messages?.[0] || 'Error checking serial number');
                                                                        setValue(`${prefix}.validating`, false);
                                                                    }
                                                                };

                                                                try {
                                                                    setValue(`${prefix}.validating`, true);
                                                                    setValue(`${prefix}.asset_not_found`, false);
                                                                    if (!window.debouncedSerialValidation) {
                                                                        window.debouncedSerialValidation = debounce((callback) => {
                                                                            callback();
                                                                        });
                                                                    }
                                                                    window.debouncedSerialValidation(validateSerialNumber);
                                                                } catch (error) {
                                                                    console.error("Error in validation:", error);
                                                                    setValue(`${prefix}.validating`, false);
                                                                    setValue(`${prefix}.asset_not_found`, false);
                                                                    resolve(error?.response?.data?.messages?.[0] || 'Error validating serial number. Please try again.');
                                                                }
                                                            });
                                                        },
                                                    }}
                                                    isLoading={watch(`${prefix}.validating`)}
                                                />
                                            </motion.div>
                                        );
                                    }
                                    return (
                                        <motion.div
                                            key={fieldItem.key}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.1 + (fieldIndex * 0.05) }}
                                        >
                                            <PavDynamicAssetField
                                                field={fieldItem}
                                                name={`${prefix}.${fieldItem.key}`}
                                            />
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                        </motion.div>
                    );
                })
            }
        </div>

        {/* <div className='flex justify-start px-4'>
            <ButtonScogoOrange
                buttonIconComponent={<span className='material-icons-outlined text-font11'>add</span>}
                textOrComponent={'Add Asset'}
                onClick={() => append({ asset_type_id: null })}
                buttonClass='py-1 w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow duration-200'
            />
        </div> */}
    </Form>
}
